import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type { CompanyWithSites } from '@/data/repos/companiesRepo';
import type {
  RefrigerationUnitInput,
  RefrigerationUnitUpdateInput,
} from '@/data/repos/refrigerationUnitsRepo';
import { useDataProvider } from '@/data/provider';
import { queryKeys } from '@/lib/query-keys';

type CompaniesQueryOptions<TData> = Omit<
  UseQueryOptions<CompanyWithSites[], unknown, TData, readonly ['companies']>,
  'queryKey' | 'queryFn'
>;

export function useCompanies<TData = CompanyWithSites[]>(options?: CompaniesQueryOptions<TData>) {
  const { companies } = useDataProvider();
  return useQuery<CompanyWithSites[], unknown, TData, readonly ['companies']>({
    queryKey: queryKeys.companies.all,
    queryFn: () => companies.listCompaniesWithSites(),
    ...options,
  });
}

export function useCompany(companyId: string | undefined) {
  const { companies } = useDataProvider();
  return useQuery({
    queryKey: companyId ? queryKeys.companies.detail(companyId) : queryKeys.companies.all,
    queryFn: async () => {
      if (!companyId) return null;
      return companies.getCompany(companyId);
    },
    enabled: Boolean(companyId),
  });
}

export function useSite(siteId: string | undefined) {
  const { sites } = useDataProvider();
  return useQuery({
    queryKey: siteId ? queryKeys.sites.detail(siteId) : queryKeys.sites.all,
    queryFn: async () => {
      if (!siteId) return null;
      return sites.getSite(siteId);
    },
    enabled: Boolean(siteId),
  });
}

const invalidateCompanySite = async (
  queryClient: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
  siteId?: string
) => {
  if (!companyId) return;
  try {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.companies.detail(companyId),
      }),
      siteId
        ? queryClient.invalidateQueries({
            queryKey: queryKeys.sites.detail(siteId),
          })
        : Promise.resolve(),
    ]);
  } catch (error) {
    // Invalidation errors should not fail mutations - the data was already saved
    console.warn('[invalidateCompanySite] Cache invalidation failed:', error);
  }
};

export function useCompanyActions(companyId: string | undefined) {
  const { actions } = useDataProvider();
  return useQuery({
    queryKey: companyId ? queryKeys.actions.byCompany(companyId) : queryKeys.actions.all,
    enabled: Boolean(companyId),
    queryFn: () => {
      if (!companyId) return Promise.resolve([]);
      return actions.listByCompany(companyId);
    },
  });
}

export function useSiteActions(companyId: string | undefined, siteId: string | undefined) {
  const { actions } = useDataProvider();
  return useQuery({
    queryKey:
      companyId && siteId ? queryKeys.actions.bySite(companyId, siteId) : queryKeys.actions.all,
    enabled: Boolean(companyId && siteId),
    queryFn: () => {
      if (!companyId || !siteId) return Promise.resolve([]);
      return actions.listBySite(companyId, siteId);
    },
  });
}

export function useReportActions(reportId: string | undefined) {
  const { actions } = useDataProvider();
  return useQuery({
    queryKey: reportId ? queryKeys.actions.byReport(reportId) : queryKeys.actions.all,
    enabled: Boolean(reportId),
    queryFn: () => {
      if (!reportId) return Promise.resolve([]);
      return actions.listByReport(reportId);
    },
  });
}

// Re-export useCreateAction from the separate hook file
export { useCreateAction } from './hooks/useCreateAction';

export function useCompleteAction(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { actions } = useDataProvider();
  return useMutation({
    mutationFn: (id: string) => actions.markCompleted(id),
    onSuccess: async () => {
      if (companyId) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.actions.byCompany(companyId),
        });
      }
      await queryClient.invalidateQueries({
        queryKey: queryKeys.actions.all,
        exact: false,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.calendar.entries(),
      });
    },
  });
}

export function useDeleteAction(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const { actions } = useDataProvider();
  return useMutation({
    mutationFn: (id: string) => actions.softDelete(id),
    onSuccess: async () => {
      if (companyId) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.actions.byCompany(companyId),
        });
      }
      await queryClient.invalidateQueries({
        queryKey: queryKeys.actions.all,
        exact: false,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.calendar.entries(),
      });
    },
  });
}

export function useCreateRefrigerationUnit(
  companyId: string | undefined,
  siteId: string | undefined
) {
  const queryClient = useQueryClient();
  const { units } = useDataProvider();
  return useMutation({
    mutationFn: async (input: RefrigerationUnitInput) => {
      if (!siteId) throw new Error('ID stabilimento richiesto');
      return units.createRefrigerationUnit(siteId, input);
    },
    onSuccess: async () => {
      await invalidateCompanySite(queryClient, companyId, siteId);
    },
  });
}

export function useUpdateRefrigerationUnit(
  companyId: string | undefined,
  siteId: string | undefined
) {
  const queryClient = useQueryClient();
  const { units } = useDataProvider();
  return useMutation({
    mutationFn: async (input: RefrigerationUnitUpdateInput) => {
      if (!siteId) throw new Error('ID stabilimento richiesto');
      if (!input.id) throw new Error('ID impianto richiesto');
      await units.updateRefrigerationUnit(siteId, input.id, input);
    },
    onSuccess: async () => {
      await invalidateCompanySite(queryClient, companyId, siteId);
    },
  });
}

export function useDeleteRefrigerationUnit(
  companyId: string | undefined,
  siteId: string | undefined
) {
  const queryClient = useQueryClient();
  const { units } = useDataProvider();
  return useMutation({
    mutationFn: async (refrigerationUnitId: string) => {
      if (!siteId) throw new Error('ID stabilimento richiesto');
      await units.deleteRefrigerationUnit(siteId, refrigerationUnitId);
    },
    onSuccess: async () => {
      await invalidateCompanySite(queryClient, companyId, siteId);
    },
  });
}

export { useCompanyPersonnel } from './hooks/useCompanyPersonnel';

// ─────────────────────────────────────────────────────────────────────────────
// Activity Hooks
// ─────────────────────────────────────────────────────────────────────────────

export interface UseContextActivitiesOptions {
  /** Filter by company ID */
  companyId?: string;
  /** Filter by site ID (requires companyId) */
  siteId?: string;
  /** Maximum activities to display (default: 5) */
  maxItems?: number;
  /** Show only urgent items - overdue or due within 7 days (default: true) */
  urgentOnly?: boolean;
}

/**
 * Hook that returns filtered activities based on context.
 * - companyId only → activities for that company
 * - companyId + siteId → activities for that site
 * - neither → all activities (top urgent)
 */
export function useContextActivities({
  companyId,
  siteId,
  maxItems = 5,
  urgentOnly = true,
}: UseContextActivitiesOptions = {}) {
  const { actions } = useDataProvider();

  const queryKey = useMemo(() => {
    if (siteId && companyId) {
      return queryKeys.actions.bySite(companyId, siteId);
    }
    if (companyId) {
      return queryKeys.actions.byCompany(companyId);
    }
    return queryKeys.actions.all;
  }, [companyId, siteId]);

  const queryFn = useMemo(() => {
    if (siteId && companyId) {
      return () => actions.listBySite(companyId, siteId);
    }
    if (companyId) {
      return () => actions.listByCompany(companyId);
    }
    return () => actions.listAll();
  }, [actions, companyId, siteId]);

  const query = useQuery({ queryKey, queryFn });

  const filteredActivities = useMemo(() => {
    const activities = query.data ?? [];
    // Only show non-completed activities
    const open = activities.filter((a) => a.status !== 'completed');

    if (urgentOnly) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

      return open
        .filter((a) => {
          if (!a.dueDate) return false;
          const due = new Date(a.dueDate);
          return due <= weekFromNow;
        })
        .sort((a, b) => new Date(a.dueDate!).getTime() - new Date(b.dueDate!).getTime())
        .slice(0, maxItems);
    }

    // If not urgent only, return all sorted by due date
    return open
      .sort((a, b) => {
        const aTime = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
        const bTime = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
        return aTime - bTime;
      })
      .slice(0, maxItems);
  }, [query.data, urgentOnly, maxItems]);

  return {
    activities: filteredActivities,
    isLoading: query.isLoading,
    total: (query.data ?? []).filter((a) => a.status !== 'completed').length,
  };
}

/**
 * Mutation to start an activity (pending → in_progress).
 */
export function useStartActivity() {
  const queryClient = useQueryClient();
  const { actions } = useDataProvider();
  return useMutation({
    mutationFn: (id: string) => actions.startActivity(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.actions.all,
        exact: false,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.calendar.entries(),
      });
    },
  });
}

/**
 * Mutation to reopen a completed activity (completed → pending).
 */
export function useReopenActivity() {
  const queryClient = useQueryClient();
  const { actions } = useDataProvider();
  return useMutation({
    mutationFn: (id: string) => actions.reopenActivity(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.actions.all,
        exact: false,
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.calendar.entries(),
      });
    },
  });
}
